import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BUNDLED_MODULE_DETAILS, type ModuleDetail } from "@/content/moduleDetails";
import { applyTextTranslations } from "@/lib/siteCollections";
import { modules as bundledModules, type ModuleDef } from "@/content/modules";
import { HARDWARE_PRODUCTS, type HardwareProduct } from "@/content/hardware";
import { glossary as bundledGlossary, type GlossaryEntry } from "@/content/glossary";
import { CUSTOMER_LOGOS, type CustomerLogo } from "@/content/customerLogos";
import { HOME_FAQS, OBJECTION_FAQS, type Faq } from "@/content/faqs";
import { useSiteCollection } from "@/hooks/useSiteCollection";

export function useModules() {
  const { items, ready } = useSiteCollection<ModuleDef>("modules", bundledModules);
  const bySlug = useMemo(() => new Map(items.map((m) => [m.slug, m])), [items]);
  return { modules: items, ready, moduleBySlug: (s: string) => bySlug.get(s) };
}

export function useHardware() {
  const { items, ready } = useSiteCollection<HardwareProduct>("hardware", HARDWARE_PRODUCTS);
  const bySlug = useMemo(() => new Map(items.map((p) => [p.slug, p])), [items]);
  return {
    products: items, ready,
    getProduct: (s: string) => bySlug.get(s),
    getRelated: (slugs: string[] = []) => slugs.map((s) => bySlug.get(s)).filter(Boolean) as HardwareProduct[],
  };
}

/** Same matching rules as the bundled getGlossaryEntry (slug, hyphen-less slug, aliases, term, full name). */
export function findGlossaryEntry(list: GlossaryEntry[], slugOrAlias: string): GlossaryEntry | undefined {
  const key = slugOrAlias.toLowerCase();
  return list.find((g) => g.slug === key) ?? list.find((g) =>
    g.slug.replace(/-/g, "") === key.replace(/-/g, "") ||
    g.aliases?.some((a) => a.toLowerCase() === key) ||
    g.term.toLowerCase() === key ||
    (g.fullName && g.fullName.toLowerCase() === key));
}

export function useGlossary() {
  const { items, ready } = useSiteCollection<GlossaryEntry>("glossary", bundledGlossary);
  const list = useMemo(() => items.map((g) => ({ ...g, short: g.shortDefinition })), [items]);
  return { glossary: list, ready, getEntry: (s: string) => findGlossaryEntry(list, s) };
}

export const useCustomerLogos = () => useSiteCollection<CustomerLogo>("customer_logos", CUSTOMER_LOGOS);
export const useHomeFaqs = () => useSiteCollection<Faq>("home_faqs", HOME_FAQS);
export const useObjectionFaqs = () => useSiteCollection<Faq>("objection_faqs", OBJECTION_FAQS);

/**
 * Detailed module page (CMS "module_details" collection, bundled fallback before import/while loading).
 * Text is overlaid with stored per-string translations for the locale; strings without one fall through to the static dictionary.
 */
export function useModuleDetail(slug: string, locale: string): { detail?: ModuleDetail; ready: boolean } {
  // Per-item override: a published CMS record replaces the bundled page text; modules without a record keep bundled text.
  const q = useQuery({
    queryKey: ["site-collection-item", "module_details", slug], staleTime: 5 * 60_000, retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase.from("site_collection_items").select("id,data").eq("collection", "module_details").eq("item_key", slug).eq("is_published", true).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const ready = !q.isLoading;
  const base = (q.data?.data as (ModuleDetail & { slug: string }) | undefined) ?? BUNDLED_MODULE_DETAILS.find((d) => d.slug === slug);
  const recordId = q.data?.id;
  const tr = useQuery({
    queryKey: ["module-detail-tr", recordId, locale],
    enabled: locale !== "en" && !!recordId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const t = await supabase.from("content_translations").select("fields").eq("table_name", "site_collection_items").eq("record_id", recordId!).eq("locale", locale).maybeSingle();
      return (t.data?.fields ?? null) as Record<string, unknown> | null;
    },
  });
  const detail = useMemo(() => base && applyTextTranslations(base, tr.data ?? undefined, new Set(["slug", "related"])), [base, tr.data]);
  return { detail, ready };
}
